import React, { createContext, useCallback, useContext, useState } from 'react'
import { OutstandingBalanceNotice } from './global-notices/outstanding-balance'

export type GlobalNoticeType = 'info' | 'warning' | 'error'
export interface GlobalNotice {
  key: string // Unique key for the notice
  type: GlobalNoticeType
  message: string | React.ReactNode
}

interface GlobalNoticesContextProps {
  readonly notices: GlobalNotice[]
  addNotice: (notice: GlobalNotice) => void
  removeNotice: (key: string) => void
}

const GlobalNoticesContext = createContext<GlobalNoticesContextProps>(
  undefined!
)

export function GlobalNoticesProvider({ children }: React.PropsWithChildren) {
  const [notices, setNotices] = useState<GlobalNotice[]>([])

  const addNotice = useCallback((notice: GlobalNotice) => {
    setNotices((prevNotices) => [
      // Replace existing notice with the same key, if any
      ...prevNotices.filter(({ key }) => key !== notice.key),
      notice,
    ])
  }, [])

  const removeNotice = useCallback((key: string) => {
    setNotices((prevNotices) =>
      prevNotices.filter((notice) => notice.key !== key)
    )
  }, [])

  return (
    <GlobalNoticesContext.Provider
      value={{
        notices,
        addNotice,
        removeNotice,
      }}
    >
      {children}

      {/* Add notice components here */}
      <OutstandingBalanceNotice />
    </GlobalNoticesContext.Provider>
  )
}

export function useGlobalNotices() {
  const context = useContext(GlobalNoticesContext)
  if (context === undefined)
    throw new Error(
      'useGlobalNotices must be used within a GlobalNoticesProvider'
    )

  return context
}
